import { useCallback, useEffect, useRef, useState } from 'react';
import { CALENDLY_CONFIG } from './calendlyConfig';

type CalendlyWidget = {
  initPopupWidget: (options: { url: string }) => void;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Calendly?: CalendlyWidget;
  }
}

const createStylesheet = (): void => {
  if (document.getElementById(CALENDLY_CONFIG.cssId)) return;

  const link = document.createElement('link');
  link.id = CALENDLY_CONFIG.cssId;
  link.rel = 'stylesheet';
  link.href = CALENDLY_CONFIG.cssSrc;
  document.head.appendChild(link);
};

const createScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(CALENDLY_CONFIG.scriptId);

    if (existingScript) {
      if (window.Calendly) {
        resolve();
      } else {
        (existingScript as HTMLScriptElement).onload = () => resolve();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = CALENDLY_CONFIG.scriptId;
    script.src = CALENDLY_CONFIG.scriptSrc;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Calendly script'));

    document.head.appendChild(script);
  });

const performLoad = async (): Promise<void> => {
  createStylesheet();
  await createScript();

  if (!window.Calendly) {
    await new Promise<void>((resolve) => {
      const checkLoaded = (): void => {
        if (window.Calendly) {
          resolve();
        } else {
          setTimeout(checkLoaded, CALENDLY_CONFIG.pollInterval);
        }
      };
      checkLoaded();
    });
  }
};

export const useCalendlyLoader = () => {
  const [status, setStatus] = useState<
    'pending' | 'loading' | 'complete' | 'error'
  >('pending');
  const [preloadError, setPreloadError] = useState<Error | null>(null);
  const loadingPromiseRef = useRef<Promise<void> | null>(null);
  const hasPreloaded = useRef(false);

  const isCalendlyAvailable = useCallback((): boolean => {
    return Boolean(window.Calendly && status === 'complete');
  }, [status]);

  const loadCalendly = useCallback(async (): Promise<void> => {
    if (isCalendlyAvailable()) {
      return Promise.resolve();
    }

    if (status === 'loading' && loadingPromiseRef.current) {
      return loadingPromiseRef.current;
    }

    const loadingPromise = performLoad()
      .then(() => {
        setStatus('complete');
        loadingPromiseRef.current = null;
      })
      .catch((error) => {
        setStatus('error');
        loadingPromiseRef.current = null;
        throw error;
      });

    setStatus('loading');
    loadingPromiseRef.current = loadingPromise;
    return loadingPromise;
  }, [isCalendlyAvailable, status]);

  const safeOpenCalendlyPopup = useCallback(
    async (url: string): Promise<void> => {
      try {
        await loadCalendly();
        if (!window.Calendly) throw new Error('Calendly not available');
        window.Calendly.initPopupWidget({ url });
      } catch {
        window.open(url, '_blank');
      }
    },
    [loadCalendly]
  );

  // Preload Calendly on hook initialization
  useEffect(() => {
    if (!hasPreloaded.current) {
      hasPreloaded.current = true;
      loadCalendly().catch((error: Error) => {
        setPreloadError(error);
      });
    }
  }, [loadCalendly]);

  return {
    loadCalendly,
    safeOpenCalendlyPopup,
    isLoaded: isCalendlyAvailable,
    isLoading: status === 'loading',
    status,
    preloadError,
  };
};
